/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import { DialogTrigger } from '@adobe/react-spectrum';
import { Dialog } from '@quarry/inventory';
import ProviderRS2 from '@react/react-spectrum/Provider';
import { chain } from '@react-aria/utils';

const PickerDialog = props => {
    const { dialogTitle, onClose, onConfirm, isOpen } = props;
    const children = React.Children.toArray(props.children);

    // We use this to keep track if the dialog was confirmed or not
    const confirmed = useRef(false);

    if (children.length !== 2) {
        console.error('Requires button and picker children.');
        return null;
    }
    const [button, picker] = children;

    const onOpenChange = isOpen => {
        if (!isOpen && !confirmed.current) {
            onClose && onClose();
        }

        if (isOpen) {
            // Reset the confirmed value if the dialog opens
            confirmed.current = false;
        }
    };

    return (
        <>
            <DialogTrigger type="fullscreen" isOpen={isOpen} onOpenChange={onOpenChange}>
                {button}
                {close => (
                    <Dialog
                        title={dialogTitle}
                        onClose={close}
                        onConfirm={() => {
                            confirmed.current = true;
                            onConfirm ? chain(onConfirm(), close()) : close();
                        }}
                    >
                        <ProviderRS2 theme="light">{picker}</ProviderRS2>
                    </Dialog>
                )}
            </DialogTrigger>
        </>
    );
};

PickerDialog.propTypes = {
    dialogTitle: PropTypes.string,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    isOpen: PropTypes.bool
};

export default PickerDialog;
